/**
 * Policy Parsing & Table of Contents Utility
 * Parses policy content checking for point form, numbering, alphabets, Roman numerals, and section titles.
 */

// Regex definitions
const NUMBERING_REGEX = /^\s*(?:(?:Section|Article|Clause|Part|Schedule)\s+)?((?:\d+\.)+\d*|\d+\.|\d+\)|\(\d+\))\s*(.*)$/i
const ROMAN_REGEX = /^\s*(\((?:i|ii|iii|iv|v|vi|vii|viii|ix|x|xi|xii)\)|(?:I|II|III|IV|V|VI|VII|VIII|IX|X|XI|XII)\.|\((?:I|II|III|IV|V|VI|VII|VIII|IX|X|XI|XII)\)|(?:i|ii|iii|iv|v|vi|vii|viii|ix|x)\.)\s*(.*)$/
const ALPHABET_REGEX = /^\s*(\([A-Za-z]\)|[A-Za-z]\.|\([A-Za-z]\)|[A-Za-z]\))\s*(.*)$/
const BULLET_REGEX = /^\s*([•\-*▪▫–—])\s*(.*)$/
const HEADER_KEYWORD_REGEX = /^(?:Section|Article|Clause|Part|Schedule|Appendix)\s+[\dA-Za-z.-]+/i

/**
 * Detects whether a string matches a policy line prefix (numbering, alphabet, roman, bullet, or header).
 */
export function detectPolicyLineKind(text) {
  if (!text || typeof text !== 'string') return null
  const trimmed = text.trim()
  if (!trimmed) return null

  // 1. Roman Numerals (e.g., I., II., (i), (ii))
  const romanMatch = trimmed.match(ROMAN_REGEX)
  if (romanMatch) {
    const prefix = romanMatch[1]
    const content = romanMatch[2]
    const isUpper = prefix.toUpperCase() === prefix
    return {
      kind: 'roman',
      prefix,
      title: content || trimmed,
      level: isUpper && !prefix.includes('(') ? 1 : 2,
      badge: `${prefix} Roman`,
    }
  }

  // 2. Numbering (e.g., 1., 1.1, 1.1.1, (1), 1))
  const numMatch = trimmed.match(NUMBERING_REGEX)
  if (numMatch) {
    const prefix = numMatch[1]
    const content = numMatch[2]
    const dotCount = (prefix.match(/\./g) || []).length
    let level = 1
    if (dotCount >= 2) level = 3
    else if (dotCount === 1 && !prefix.endsWith('.')) level = 2
    else if (prefix.startsWith('(') || prefix.endsWith(')')) level = 2

    return {
      kind: 'number',
      prefix,
      title: content || trimmed,
      level,
      badge: `${prefix} Number`,
    }
  }

  // 3. Alphabets (e.g., A., B., (a), (b), a))
  const alphaMatch = trimmed.match(ALPHABET_REGEX)
  if (alphaMatch) {
    const prefix = alphaMatch[1]
    const content = alphaMatch[2]
    const rawChar = prefix.replace(/[^A-Za-z]/g, '')
    const isUpper = rawChar === rawChar.toUpperCase()
    return {
      kind: 'alphabet',
      prefix,
      title: content || trimmed,
      level: isUpper && !prefix.includes('(') ? 1 : 2,
      badge: `${prefix} Alphabet`,
    }
  }

  // 4. Point Form / Bullets (e.g., •, -, *, ▪)
  const bulletMatch = trimmed.match(BULLET_REGEX)
  if (bulletMatch) {
    const prefix = bulletMatch[1]
    const content = bulletMatch[2]
    return {
      kind: 'point',
      prefix,
      title: content || trimmed,
      level: 2,
      badge: `${prefix} Point Form`,
    }
  }

  // 5. Section Keyword (e.g. "Section 1: ...")
  if (HEADER_KEYWORD_REGEX.test(trimmed)) {
    return {
      kind: 'header',
      prefix: '',
      title: trimmed,
      level: 1,
      badge: 'Section Header',
    }
  }

  // 6. Generic Header Heuristic: short line, ALL-CAPS or ending with ":"
  const isShort = trimmed.length < 80
  const isAllUpper = isShort && trimmed === trimmed.toUpperCase() && /[A-Z]/.test(trimmed)
  const endsWithColon = isShort && trimmed.endsWith(':')
  const isLikelyHeaderTitle = isShort && !trimmed.endsWith('.') && !trimmed.includes(',') && trimmed.split(' ').length <= 8

  if (isAllUpper || endsWithColon || isLikelyHeaderTitle) {
    return {
      kind: 'header',
      prefix: '',
      title: trimmed,
      level: isAllUpper ? 1 : 2,
      badge: 'Header',
    }
  }

  return null
}

/**
 * Parses raw pasted text into structured policy content blocks.
 */
export function parseRawTextToBlocks(rawText) {
  if (!rawText || !rawText.trim()) return []

  const lines = rawText
    .split(/\r?\n/)
    .map(l => l.trim())
    .filter(Boolean)

  return lines.map((line, idx) => {
    const detected = detectPolicyLineKind(line)

    if (detected) {
      if (detected.kind === 'point') {
        return {
          type: 'point',
          text: line,
          kind: detected.kind,
          level: detected.level,
        }
      }
      if (detected.kind === 'header' || detected.kind === 'number' || detected.kind === 'alphabet' || detected.kind === 'roman') {
        return {
          type: detected.level === 1 ? 'header' : 'subheader',
          text: line,
          kind: detected.kind,
          level: detected.level,
        }
      }
    }

    return {
      type: 'paragraph',
      text: line,
    }
  })
}

/**
 * Extracts Table of Contents items from policy blocks.
 * Default headersOnly: true ensures only main headers are shown, excluding subheaders and point form.
 */
export function extractPolicyTOC(blocks = [], options = { headersOnly: true }) {
  if (!Array.isArray(blocks)) return []

  const items = []
  const headersOnly = options?.headersOnly !== false

  blocks.forEach((block, index) => {
    if (!block || !block.text) return
    const text = block.text.trim()
    if (!text) return

    const isExplicitHeader = block.type === 'header' || block.type === 'h2' || block.type === 'h3'
    const isSubHeader = block.type === 'subheader' || block.type === 'h4'
    const isPoint = block.type === 'point'
    const detected = detectPolicyLineKind(text)

    let isTOCEntry = false
    if (headersOnly) {
      // ONLY show the header, not subheader of point form
      isTOCEntry = isExplicitHeader || (!isSubHeader && !isPoint && (
        detected?.kind === 'header' ||
        (detected?.level === 1 && detected?.kind === 'number')
      ))
    } else {
      isTOCEntry = isExplicitHeader || isSubHeader || (detected && (
        detected.kind === 'number' ||
        detected.kind === 'alphabet' ||
        detected.kind === 'roman' ||
        detected.kind === 'header' ||
        (detected.kind === 'point' && text.length < 100)
      ))
    }

    if (isTOCEntry) {
      const level = 1
      const kind = detected ? detected.kind : (isExplicitHeader ? 'header' : 'section')
      const prefix = detected ? detected.prefix : ''
      const badge = detected ? detected.badge : 'Header'

      // Generate a clean URL-friendly anchor ID
      const cleanSlug = text
        .slice(0, 32)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')

      const id = `toc-sec-${index}-${cleanSlug || 'section'}`

      items.push({
        id,
        index,
        text,
        title: detected?.title || text,
        prefix,
        kind,
        level,
        badge,
      })
    }
  })

  return items
}

/**
 * Standard sample policy demonstrating numbering, alphabets, and point forms.
 */
export const SAMPLE_POLICY_DATA = {
  name: "Personal Data Protection & Governance Policy (Sample)",
  content: [
    { type: "header", text: "1.0 INTRODUCTION & PURPOSE" },
    { type: "paragraph", text: "Bedok Methodist Church ('BMC') is committed to protecting the privacy and personal data of our members, worshippers, visitors, and staff in accordance with the Singapore Personal Data Protection Act (PDPA)." },
    { type: "subheader", text: "1.1 Scope & Applicability" },
    { type: "paragraph", text: "This policy governs all personal data collected, used, disclosed, or retained by BMC across church services, ministries, cell groups, and digital platforms." },
    { type: "header", text: "2.0 DATA COLLECTION PRINCIPLES" },
    { type: "subheader", text: "A. Types of Personal Data Collected" },
    { type: "point", text: "• Identification details including Full Name, NRIC/FIN (masked), and Date of Birth" },
    { type: "point", text: "• Contact details including residential address, email address, and mobile number" },
    { type: "point", text: "• Pastoral care information, baptism records, and membership particulars" },
    { type: "subheader", text: "B. Purpose of Collection & Use" },
    { type: "paragraph", text: "We collect personal data only for purposes that a reasonable person would consider appropriate in the circumstances, including:" },
    { type: "subheader", text: "(a) Pastoral care, ministry administration, and cell group fellowship" },
    { type: "subheader", text: "(b) Maintaining church membership rolls and electoral registers" },
    { type: "subheader", text: "(c) Processing tithes, pledges, and issuing tax exemption receipts" },
    { type: "header", text: "3.0 RIGHTS OF DATA SUBJECTS" },
    { type: "subheader", text: "3.1 Access and Correction Requests" },
    { type: "paragraph", text: "Individuals have the right to request access to or correction of their personal data held by BMC by submitting a written request to the Data Protection Officer." },
    { type: "subheader", text: "3.2 Withdrawal of Consent" },
    { type: "paragraph", text: "You may withdraw consent for data processing at any time by giving reasonable notice to the Church Office." },
    { type: "header", text: "4.0 DATA PROTECTION OFFICER (DPO) INQUIRIES" },
    { type: "paragraph", text: "For queries, feedback, or complaints regarding this policy or PDPA compliance, please contact our appointed DPO at dpo@bmc.org.sg." }
  ]
}
